<script setup lang="ts">
import 'vue-tel-input/vue-tel-input.css'

import { FormSchema } from '~/components/FormContactUs__utils'

type TBlogPost = {
  data: {
    image: string
    link: string
    title: string
    description: string
    tags: string[]
  }
}

type TProps = TBuilderBaseProps & {
  blogPosts?: TBlogPost[]
}

const props = withDefaults(defineProps<TProps>(), {
  blogPosts: () => [],
  attributes: () => ({}),
})

const blogPostsReduced = computed(() => {
  if (!props.blogPosts || !props.blogPosts.length) {
    return []
  }

  return props.blogPosts.map((postData) => ({ ...postData.data }))
})

type FormData = Partial<
  Pick<
    SendContactRequestArgs,
    | 'first_name'
    | 'last_name'
    | 'email'
    | 'country_id'
    | 'message'
    | 'phone'
    | 'company_name'
  >
>

const formData = reactive<FormData>({
  first_name: '',
  last_name: '',
  email: '',
  country_id: 0,
  phone: '',
  company_name: '',
  message: '',
})

const countryName = ref('')

const { validate, isValid, scrollToError, getError } = useZodFormValidation(
  FormSchema,
  formData
)

const getFormError = (key: keyof FormData) => {
  return getError(key) || ''
}

const formSubmissionLoading = ref(false)
const formSubmitted = ref(false)

const { getToken: getCaptchaToken } = useTurnstile()
const toast = useTrailToast()

const route = useRoute()

const { proxy } = useScriptGoogleTagManager()

const GTMEventOnFormSubmission = () => {
  proxy.dataLayer.push({
    event: 'contact_sales',
    target: null,
    action: null,
    'target-properties': null,
    value: null,
    'interaction-type': false,
    contact_request_details: {
      country: countryName.value || 'N/a',
      company_name: formData.company_name || 'N/a',
    },
    contact_request_status: 'completed',
    click_source: route.path,
  })
}

const onFormSubmit = async () => {
  formSubmissionLoading.value = true

  try {
    await validate()
    if (!isValid.value) {
      nextTick(() =>
        scrollToError('.validation-error', {
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        })
      )
      return
    }

    await $fetch('/api/v1/leads/contact-us', {
      method: 'POST',
      headers: captchaHeaders(await getCaptchaToken()),
      body: {
        ...formData,
        utm_campaign: route.query?.utm_campaign || null,
      },
    })

    GTMEventOnFormSubmission()

    formSubmitted.value = true
  } catch (error) {
    console.error('Form submission failed:', error)
    formSubmitted.value = false

    const fetchError = error as { statusCode?: number }

    if (isCaptchaRejection(fetchError?.statusCode, fetchError)) {
      toast.negative({ content: CAPTCHA_COPY.retry })
    }
  } finally {
    formSubmissionLoading.value = false
  }
}

const config = useRuntimeConfig()

const { links } = config.public
</script>

<template>
  <div
    id="form-contact-us"
    class="w-full min-h-[700px] p-8 !bg-white rounded-2xl inline-flex flex-col justify-start items-start"
  >
    <transition name="fade-in-only" tag="div">
      <form
        v-if="!formSubmitted"
        class="w-full !bg-white rounded-2xl inline-flex flex-col justify-start items-start gap-6"
        @submit.prevent="onFormSubmit"
      >
        <div class="w-full flex flex-col justify-start items-start gap-4">
          <div
            class="inline-flex flex-col md:flex-row justify-start items-start self-stretch gap-2"
          >
            <div class="w-full">
              <LazyTrailDSInputField
                v-model.trim="formData.first_name"
                label="First name"
                placeholder="Type your first name"
                :hint="getFormError('first_name')"
                :error="!!getFormError('first_name')"
                required
                :class="{ 'validation-error': getFormError('first_name') }"
                data-testid="form-contact-us-first-name"
              />
            </div>

            <div class="w-full">
              <LazyTrailDSInputField
                v-model.trim="formData.last_name"
                label="Last name"
                placeholder="Type your last name"
                :hint="getFormError('last_name')"
                :error="!!getFormError('last_name')"
                required
                :class="{ 'validation-error': getFormError('last_name') }"
                data-testid="form-contact-us-last-name"
              />
            </div>
          </div>

          <div
            class="self-stretch flex flex-col justify-start items-start gap-3"
          >
            <LazyTrailDSInputField
              v-model.trim="formData.email"
              label="Email"
              placeholder="Type your email"
              :hint="getFormError('email')"
              :error="!!getFormError('email')"
              required
              :class="{ 'validation-error': getFormError('email') }"
              class="w-full"
              data-testid="form-contact-us-email"
            />
          </div>

          <div
            class="self-stretch flex flex-col justify-start items-start gap-3"
          >
            <LazyTrailDSFormLabel
              :error-message="getFormError('country_id')"
              :class="{ 'validation-error': getFormError('country_id') }"
              required
            >
              Country
            </LazyTrailDSFormLabel>

            <LazyTrailDSCountrySelect
              v-model="formData.country_id"
              v-model:country-name="countryName"
              placeholder="Select your country"
              button-class="bg-transparent"
              class="w-full"
              data-testid="form-contact-us-country-select"
            />
          </div>

          <div
            class="self-stretch min-w-80 flex flex-col justify-start items-start gap-3"
          >
            <LazyTrailDSFormLabel
              :error-message="getFormError('phone')"
              :class="{
                'validation-error': getFormError('phone'),
                'text-trail-caption text-trail-text-caption-normal': true,
              }"
            >
              Phone number
            </LazyTrailDSFormLabel>

            <ClientOnly fallback-tag="div">
              <vue-tel-input
                v-model="formData.phone"
                auto-default-country
                valid-characters-only
                auto-format
                mode="international"
                :dropdown-options="{
                  showFlags: true,
                  showSearchBox: true,
                  showDialCodeInSelection: true,
                }"
                :input-options="{
                  showDialCode: false,
                  maxlength: 15,
                  styleClasses:
                    'text-base placeholder:text-grey-500 bg-trail-surface-tertiary-normal',
                  placeholder: 'Phone number',
                }"
                class="h-12 w-full !rounded-lg !bg-trail-surface-tertiary-normal !border-seashell-900 input-control"
                data-testid="form-contact-us-phone-number"
              />

              <template #fallback>
                <LazyBaseSkeleton class="w-full h-12 rounded-lg" />
              </template>
            </ClientOnly>
          </div>

          <div class="w-full flex flex-col justify-start items-start gap-3">
            <div
              class="self-stretch inline-flex justify-start items-start gap-1"
            >
              <LazyTrailDSInputField
                v-model.trim="formData.company_name"
                label="Company name"
                placeholder="Type your company name"
                :hint="getFormError('company_name')"
                :error="!!getFormError('company_name')"
                optional-text="(Optional)"
                :class="{ 'validation-error': getFormError('company_name') }"
                class="w-full"
                data-testid="form-contact-us-company-name"
              />
            </div>
          </div>

          <div
            data-show-title="true"
            data-variant="Default"
            class="self-stretch min-w-80 flex flex-col justify-start items-start gap-3"
          >
            <LazyTrailDSFormLabel
              :error-message="getFormError('message')"
              :class="{ 'validation-error': getFormError('message') }"
              required
            >
              Message
            </LazyTrailDSFormLabel>

            <LazyTrailDSTextField
              v-model="formData.message"
              placeholder="Tell us about your business and how we can assist you—any details that will help us tailor our services to your needs."
              :error="!!getFormError('message')"
              :hint="getFormError('message')"
              data-testid="form-contact-us-message"
              class="h-32 w-full"
            />
          </div>

          <div
            class="bg-Surface-Primary-Highlight rounded-lg inline-flex justify-center items-center gap-2"
          >
            <LazyTrailDSButton
              :loading="formSubmissionLoading"
              :disabled="formSubmissionLoading"
              class="w-full h-full"
              data-testid="form-contact-us-submit-button"
              @click.prevent="onFormSubmit"
            >
              <span>Submit</span>
            </LazyTrailDSButton>
          </div>

          <client-only>
            <TurnstileWidget />
          </client-only>

          <div class="self-stretch justify-center">
            <span
              class="text-trail-text-title-disabled text-xs font-normal font-['IBM_Plex_Sans'] leading-snug"
              >By clicking submit, you agree to our
            </span>

            <span
              class="text-trail-text-title-disabled text-xs font-normal font-['IBM_Plex_Sans'] underline leading-snug"
            >
              <a :href="links.privacyPolicy" target="_blank">Privacy policy</a>
            </span>

            <span
              class="text-trail-text-title-disabled text-xs font-normal font-['IBM_Plex_Sans'] leading-snug"
            >
              and
            </span>

            <span
              class="text-trail-text-title-disabled text-xs font-normal font-['IBM_Plex_Sans'] underline leading-snug"
            >
              <a :href="links.tnc" target="_blank"> Terms and conditions</a>
            </span>
          </div>
        </div>
      </form>

      <LazyFormContactUsSubmission v-else>
        <template v-if="props.blogPosts.length" #blogPosts>
          <div class="inline-flex flex-col gap-6 w-full">
            <LazyBaseCard
              v-for="(post, index) in blogPostsReduced"
              :key="index"
              orientation="horizontal"
              class="!justify-start"
            >
              <template #image>
                <img
                  :src="post.image"
                  alt="Blog Post Image"
                  class="hidden md:inline-flex w-1/3 h-auto object-cover rounded-tl-2xl rounded-bl-2xl"
                  loading="lazy"
                />
              </template>

              <template v-if="post.title" #title>
                <h4 class="text-trail-body2">{{ post.title }}</h4>
              </template>

              <div class="text-trail-text-body-normal text-trail-body3">
                {{ post.description }}
              </div>

              <template #link>
                <a
                  :href="post.link"
                  class="text-trail-body-link3"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              </template>

              <template v-if="post.tags && post.tags.length" #footer>
                <div class="inline-flex flex-wrap gap-1">
                  <LazyBaseBadge
                    v-for="(tag, tagIndex) in post.tags"
                    :key="`${tagIndex}-${tag}`"
                    class="bg-trail-surface-primary-highlight text-white text-trail-caption font-medium rounded-full"
                  >
                    {{ tag }}
                  </LazyBaseBadge>
                </div>
              </template>
            </LazyBaseCard>
          </div>
        </template>
      </LazyFormContactUsSubmission>
    </transition>
  </div>
</template>
